import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import DefaultPage from './DefaultPage';
import styles from '../styles/pages/ChoosePanel.module.css';
import InfoBox from '../components/InfoBox';
import Box from '../components/Box';
import Progress from '@ax2/common/components/Progress';
import Dropdown from '@ax2/common/components/Dropdown';
import { ActionButton } from '@ax2/common/components/ActionButton';
import {
  getDropdownOptions,
  PanelQuestion,
  usePanelRecommendationUpdateContext,
} from '../contexts/PanelRecommendationContext';

function getQuestion(step: PanelQuestion): string {
  switch (step) {
    case 'q1':
      return '1. Which option best targets your key area of interest?';
    case 'q2':
      return '2. Which option best describes the pharmaceutical drug you will be referencing in your survey?';
  }
}

const ChoosePanel: React.FC<RouteComponentProps> = ({ history }) => {
  const [answer1, setAnswer1] = useState<number | null>(null);
  const [answer1Temp, setAnswer1Temp] = useState<number | null>(null);
  const [answer2, setAnswer2] = useState<number | null>(null);
  const [answer2Temp, setAnswer2Temp] = useState<number | null>(null);
  const [step, setStep] = useState<PanelQuestion>('q1');
  const setAnswers = usePanelRecommendationUpdateContext();

  const goTo = useCallback((step: PanelQuestion) => {
    setStep(step);
    if (step === 'q1') {
      setAnswer1(null);
      setAnswer2(null);
    }
    if (step === 'q2') {
      setAnswer2(null);
    }
  }, []);

  // If we're not on a specific step, but rather on a root URL, go to 1st step
  useEffect(() => {
    if (answer1 === null) {
      goTo('q1');
    }
  }, [answer1, answer2, history, step, goTo]);

  const backOnClick = () => {
    if (step === 'q2') {
      goTo('q1');
    }
  };

  const nextOnClick = () => {
    if (step === 'q1') {
      setAnswer1(answer1Temp);
      goTo('q2');
    } else if (step === 'q2') {
      setAnswer2(answer2Temp);
      if (answer1 !== null && answer2Temp !== null) {
        setAnswers({
          q1: answer1,
          q2: answer2Temp,
        });
        history.push('/shop');
      }
    }
  };

  return (
    <DefaultPage
      header={{
        title: 'Panel Recommendation Questionnaire',
        subtitle: 'Find the right panel to answer your questions.',
        type: 'secondary',
      }}
    >
      <InfoBox
        header="First:"
        text={
          <p>
            Answer the following two questions so we can suggest the optimal
            panel for you.
          </p>
        }
      />
      <Box>
        <div className={styles.mainPanel}>
          <div className={styles.controls}>
            <div className={styles.progressContainer}>
              <Progress
                percentage={step === 'q1' ? 50 : 100}
                fullWidth={true}
              />
            </div>
            <div className={styles.question}>
              <p className={styles.questionText}>{getQuestion(step)}</p>
              <p className={styles.questionInstruction}>Select one.</p>
              <Dropdown
                options={getDropdownOptions(step)}
                value={
                  step === 'q1'
                    ? answer1Temp || undefined
                    : answer2Temp || undefined
                }
                onChange={value => {
                  if (step === 'q1') {
                    setAnswer1Temp(value);
                  } else if (step === 'q2') {
                    setAnswer2Temp(value);
                  }
                }}
              />
            </div>
            <div className={styles.buttons}>
              <ActionButton
                type="secondary"
                onClick={backOnClick}
                disabled={step === 'q1'}
                text="Back"
              />
              <ActionButton
                onClick={nextOnClick}
                disabled={
                  (step === 'q1' && answer1Temp === null) ||
                  (step === 'q2' && answer2Temp === null)
                }
                text={
                  step === 'q1' ? 'Next question' : 'View panel recommendations'
                }
              />
            </div>
          </div>
        </div>
      </Box>
    </DefaultPage>
  );
};

export default withRouter(ChoosePanel);
